import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Stack,
  useTheme,
} from '@mui/material';
import {
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
} from '@mui/icons-material';

const CourseQuestionMap = () => {
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [selectedCourse, setSelectedCourse] = useState('');
  const [filteredData, setFilteredData] = useState([]);

  // Static data for dropdowns
  const staticCourseData = [
    { id: 1, name: 'Mathematics' },
    { id: 2, name: 'Science' },
    { id: 3, name: 'History' },
  ];

  // Static data for the table
  const staticQuestionData = {
    Mathematics: [
      { sNo: 1, module: 'Algebra', question: 'Solve for x: 2x + 3 = 7' },
      { sNo: 2, module: 'Geometry', question: 'Find the area of a circle with radius 5.' },
    ],
    Science: [
      { sNo: 1, module: 'Physics', question: 'What is the law of inertia?' },
      { sNo: 2, module: 'Chemistry', question: 'What is the pH of pure water?' },
    ],
    History: [],
  };

  const handleCourseChange = (event) => {
    setSelectedCourse(event.target.value);
    setPage(1); // Reset to the first page when course changes
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // Reset to page 1 when rows per page changes
  };

  useEffect(() => {
    setFilteredData(selectedCourse ? staticQuestionData[selectedCourse] || [] : []);
  }, [selectedCourse]);

  const paginatedData = filteredData.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <FormControl sx={{ width: 300, maxWidth: '100%' }}>
          <InputLabel id="course-label" sx={{ color: theme.palette.text.primary }}>
            Course Name
          </InputLabel>
          <Select
            labelId="course-label"
            value={selectedCourse}
            label="Course Name"
            onChange={handleCourseChange}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.text.primary,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
            }}
          >
            <MenuItem value="">
              <em>Select a course</em>
            </MenuItem>
            {staticCourseData.map((course) => (
              <MenuItem key={course.id} value={course.name}>
                {course.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        {selectedCourse && (
          <>
            {filteredData.length > 0 ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <TableContainer sx={{ width: '100%', borderRadius: '16px' }}>
                  <Table
                    sx={{
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        fontWeight: 'bold',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Module</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>Question</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedData.map((question, index) => (
                        <TableRow
                          key={question.sNo}
                          sx={{
                            backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                          }}
                        >
                          <TableCell sx={{ fontSize: '1rem' }}>{question.sNo}</TableCell>
                          <TableCell sx={{ fontSize: '1rem' }}>{question.module}</TableCell>
                          <TableCell sx={{ fontSize: '1rem' }}>{question.question}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center', mt: 4, p: 2 }}>
                <Typography variant="h6" color={theme.palette.text.primary}>
                  No Record Found
                </Typography>
              </Box>
            )}
          </>
        )}
      </Paper>

      {selectedCourse && filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>
                Rows
              </InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}
    </Container>
  );
};

export default CourseQuestionMap;